// downloadService.js
//import * as XLSX from 'xlsx';
import { loadXlsxStyleForExport } from './xlsx';
import { getHealthCareOrgName, isMultiTinVal } from './common.service';
import { networkManagementConstants } from '@src/constants/network-management-constants';
import { StorageService } from './storage.service';
export interface DownloadOptions {
  fileName: string;
  fileFormat: string;
  journeyName?: string;
  metricName?: string;
  filters?: any;
  noStyling?: boolean;
}
class DownloadService {
  static pushValuetoArrSub(arr, params, key) {
    for (const filterKey in params[key]) {
      if (Object.prototype.hasOwnProperty.call(params[key], filterKey)) {
        arr.push({
          key: filterKey.toUpperCase(),
          value: params[key][filterKey],
        });
      }
    }
  }

  static pushValuetoArr(arr, params) {
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        if (key === 'filters') {
          this.pushValuetoArrSub(arr, params, key);
        } else {
          arr.push({ key: key.toUpperCase(), value: params[key] });
        }
      }
    }
  }

  static async download(data, options) {
    const XLSXStyle = await loadXlsxStyleForExport();
    if (!XLSXStyle) return;
    const { sheets } = data;
    const wb = XLSXStyle.utils.book_new();

    this.appendCoverPage(data, options, wb, XLSXStyle);

    sheets.forEach((sheet) => {
      const ws = XLSXStyle.utils.json_to_sheet(sheet.data);

      const range = XLSXStyle.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSXStyle.utils.encode_cell({ r: 0, c: C });
        const cell = ws[cellAddress];
        if (cell) {
          const style = {
            font: { bold: true },
          };
          cell.s = style;
        }

      }
      XLSXStyle.utils.book_append_sheet(wb, ws, sheet.title.substr(0, 30));
    });
    XLSXStyle.writeFile(wb, options.fileName + options.fileFormat);
  }

  static async downloadOnDemandReport(data, options, hcoName) {
    const XLSXStyle = await loadXlsxStyleForExport();
    if (!XLSXStyle) return;
    const { sheets } = data;
    const wb = XLSXStyle.utils.book_new();

    this.appendCoverPageOnDemand(data, options, wb, XLSXStyle, hcoName);

    sheets.forEach((sheet) => {
      const ws = XLSXStyle.utils.json_to_sheet(sheet.data);

      const range = XLSXStyle.utils.decode_range(ws['!ref']);

      // Apply bold style to header row
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSXStyle.utils.encode_cell({ r: 0, c: C });
        const cell = ws[cellAddress];
        if (cell) {
          cell.s = { font: { bold: true } };
        }
      }

      // Apply comma number format to numeric data cells so Excel displays
      // commas (e.g. 1,234.56) while keeping the value numeric and summable
      for (let R = range.s.r + 1; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSXStyle.utils.encode_cell({ r: R, c: C });
          const cell = ws[cellAddress];
          if (cell && cell.t === 'n') {
            cell.s = { numFmt: '#,##0.00' };
          }
        }
      }

      XLSXStyle.utils.book_append_sheet(wb, ws, sheet.title.substr(0, 30));
    });
    XLSXStyle.writeFile(wb, options.fileName + options.fileFormat);
  }

  static async downloadDDP(data: any, options: DownloadOptions) {
    const XLSXStyle = await loadXlsxStyleForExport();
    if (!XLSXStyle) return;
    const { sheets } = data;
    const wb = XLSXStyle.utils.book_new();

    this.appendCoverPage(data, options, wb, XLSXStyle);

    // Append sheet containing data of the table
    sheets.forEach((sheet: any) => {
      const ws = XLSXStyle.utils.json_to_sheet(sheet.data);

      // Apply bold style to the first row
      const range = XLSXStyle.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSXStyle.utils.encode_cell({ r: 0, c: C });
        this.applyBoldStyle(ws, cellAddress);
      }
      // Add a one-row gap between tables in the same sheet
      ws[XLSXStyle.utils.encode_cell({ r: range.e.r + 1, c: 0 })] = { t: 's', v: '' };

      // Add additional data arrays (data1, data2, etc.) if they exist
      for (let i = 1; sheet[`data${i}`]; i++) {
        // Add a one-row gap between tables in the same sheet
        XLSXStyle.utils.sheet_add_json(ws, [{}], {
          header: [''], // Skip header
          skipHeader: true,
          origin: -1 // Add data at the end of the sheet
        });

        // Append additional data to the same sheet
        const wsData = XLSXStyle.utils.json_to_sheet(sheet[`data${i}`]);
        XLSXStyle.utils.sheet_add_json(ws, XLSXStyle.utils.sheet_to_json(wsData), {
          header: Object.keys(sheet[`data${i}`][0]),
          skipHeader: false,
          origin: -1 // Add data at the end of the sheet
        });
        const additionalRange = XLSXStyle.utils.decode_range(ws['!ref']);
        for (let C = additionalRange.s.c; C <= additionalRange.e.c; ++C) {
          const cellAddress = XLSXStyle.utils.encode_cell({ r: range.e.r + 2, c: C });
          this.applyBoldStyle(ws, cellAddress);
        }
      }
      XLSXStyle.utils.book_append_sheet(wb, ws, sheet.title.substr(0, 30));
    });
    XLSXStyle.writeFile(wb, options.fileName + options.fileFormat);
    //this.sessionService.setAppEventDataLayerForAdobeOnExportData(options.fileName);
  }

  static applyBoldStyle(ws: any, cellAddress: any) {
    const cell = ws[cellAddress];
    const boldStyle = {
      font: { bold: true }
    };
    if (!cell.s) {
      cell.s = boldStyle;
    } else {
      cell.s = { ...cell.s, ...boldStyle };
    }
  }
  static getTimePeriod(data) {
    return data.sheets[0].title === 'Knowledge Center Manager'
      ? { 'Time Period': 'N/A' }
      : { 'Time Period': '6 Months' };
  }

  static appendCoverPage(data, options, wb, xlsx) {
    options.fileName = options.fileName || 'download';
    options.fileFormat = options.fileFormat || '.xlsx';
    const defaultName = 'Sample';
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();

    const date = mm + '/' + dd + '/' + yyyy;
    const time = new Date().toLocaleTimeString('en-US');
    const tinName = options.tinName
    const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
    const parentEntity = getHealthCareOrgName();
    const userName = currentUser?.length ? currentUser[0].FirstName + ' ' + currentUser[0].LastName : null;
    let capability = options.journeyName || defaultName;
    const metric = options.metricName || defaultName;
    const filters = options.filters
      ? options.filters
      : this.getTimePeriod(data);
    let params;
    if (options.metricName === 'Emergency Department Claims Analyzer Details') {
      params = { 'Parent Entity': parentEntity, date, time, userName, capability, metric, filters };
    } else if (options?.fileName === 'Adv Pract Exemptions') {
      capability = 'Advanced Practice Health Care Provider Exemptions';
      params = options.filters
        ? { 'Parent Entity': parentEntity, date, time, userName, capability, filters }
        : { date, time, userName, capability };
    }
    else if (options?.metricName === 'Initial Adverse Determinations') {
      params = options.filters
        ? { filters, date, time, userName, capability, metric }
        : { date, time, userName, capability };
    } else if (options?.metricName === 'Exemption Summary' && options?.tinMode) {
      params = { tinName, date, time, userName, capability, metric, filters }
    }
    else if (options?.metricName === 'Exemption Summary' && !options?.tinMode) {
      params = { 'Parent Entity': options.hcoName, date, time, userName, capability, metric, filters }
    }
    else if (options?.metricName === 'Health Systems Details' && options?.tinMode) {
      params = { tinName, date, time, userName, capability, filters }
    }
    else if ((options?.metricName === 'Parent Entity Details' && !options?.tinMode) || options?.fileName === networkManagementConstants.exportVbc.vbcActivePrograms) {
      params = { 'Parent Entity': options.hcoName, date, time, userName, capability, filters }
    }
    else {
      params = { 'Parent Entity': parentEntity, date, time, userName, capability, metric, filters };
    }

    const coverPageData = [{}, {}, {}, {}, {}, {}];
    this.pushValuetoArr(coverPageData, params);

    const wsLabel = xlsx.utils.json_to_sheet(coverPageData, {
      skipHeader: true,
    });
    xlsx.utils.book_append_sheet(wb, wsLabel, 'Cover Page');
  }

  static appendCoverPageOnDemand(data, options, wb, xlsx, hcoName) {
    options.fileName = options.fileName || 'download';
    options.fileFormat = options.fileFormat || '.xlsx';
    const defaultName = 'Sample';
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();

    const date = mm + '/' + dd + '/' + yyyy;
    const time = new Date().toLocaleTimeString('en-US');
    const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
    const userName = currentUser?.length ? currentUser[0].FirstName + ' ' + currentUser[0].LastName : null;
    let capability = options.journeyName || defaultName;
    const metric = options.metricName || defaultName;
    const filters = options.filters
      ? options.filters
      : this.getTimePeriod(data);
    let params;

    params = { 'Parent Entity': hcoName, date, time, userName, capability, metric, filters };

    const coverPageData = [{}, {}, {}, {}, {}, {}];
    this.pushValuetoArr(coverPageData, params);

    const wsLabel = xlsx.utils.json_to_sheet(coverPageData, {
      skipHeader: true,
    });
    xlsx.utils.book_append_sheet(wb, wsLabel, 'Cover Page');
  }

  static async downloadV2(data: any, options: DownloadOptions) {
    const XLSXStyle = await loadXlsxStyleForExport();
    if (!XLSXStyle) return;
    const { sheets } = data;
    const wb = XLSXStyle.utils.book_new();
    this.appendCoverPage(data, options, wb, XLSXStyle);
    // Append sheet containing data of the table
    sheets.forEach((sheet: any) => {
      const ws: XLSXStyle.WorkSheet = XLSXStyle.utils.json_to_sheet([]);
      let dataOrigin = 'A1';

      // Only add the "***INTERNAL USE ONLY***" heading if noStyling is false or undefined
      if (!options.noStyling) {
        const Heading = [['***INTERNAL USE ONLY***']];
        // Adding styles for column headers
        const headerStyle = {
          fill: { fgColor: { rgb: 'FFFF00' } } // Yellow background color
        };
        ws['!merges'] = ws['!merges'] || [];
        ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: Object.keys(sheet?.data[0]).length - 1 } });
        XLSXStyle.utils.sheet_add_aoa(ws, Heading, { origin: 'A1' }); // Adding header with styles

        // Applying styles to column headers
        const headerRange = XLSXStyle.utils.decode_range(ws['!ref']);

        for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
          const cell = XLSXStyle.utils.encode_cell({ r: headerRange.s.r, c: col });

          ws[cell].s = headerStyle;
        }
        dataOrigin = 'A2';
      }

      const ws2 = XLSXStyle.utils.sheet_add_json(ws, sheet.data, { origin: dataOrigin, skipHeader: false }); ws2['!cols'] = [];
      Object.keys(sheet.data[0]).forEach((key, colIndex) => {
        const headerRowIndex = options.noStyling ? 0 : 1;
        const cellAddress = XLSXStyle.utils.encode_cell({ r: headerRowIndex, c: colIndex });
        // Apply styling based on noStyling option
        const cellStyle: any = {
          alignment: { wrapText: true, horizontal: 'center' },
          font: { bold: true }
        };

        if (!options.noStyling) {
          cellStyle.fill = { fgColor: { rgb: this.getLobColor(ws2[cellAddress].v) } };
        }

        ws2[cellAddress].s = cellStyle;
        if (ws2['!cols']) {
          ws2['!cols'].push({ width: 15 });
        }
      });
      XLSXStyle.utils.book_append_sheet(wb, ws, sheet.title.substr(0, 30));
    });
    XLSXStyle.writeFile(wb, options.fileName + options.fileFormat);
    //this.sessionService.setAppEventDataLayerForAdobeOnExportData(options.fileName);

  }
  static getLobColor(key) {
    if (key.includes('Excluded')) {
      return 'FDE9D9';
    } else if (key.includes('Excl Eff Date')) {
      return 'D8E4BC';
    } else {
      return 'D9D9D9';
    }
  }

  static getsummaryImpactColor(key) {
    return 'D8E1F2';
  }

  // Mirrors Angular's downloadV3: colored group-header row + sub-header row for the affordability summary export.
  static async downloadV3(data: any, options: DownloadOptions) {
    const XLSXStyle = await loadXlsxStyleForExport();
    if (!XLSXStyle) return;
    const { sheets } = data;
    const wb = XLSXStyle.utils.book_new();
    this.appendCoverPage(data, options, wb, XLSXStyle);

    sheets.forEach((sheet: any) => {
      const Heading = [
        [
          'PROVIDER SYSTEM',
          'Hospital Reference Lab',
          'Site of Service',
          'Medication Sourcing',
          'Designated Diagnostic Provider',
          'IMPACT UPON REQUEST ONLY',
        ],
      ];

      const ws: XLSXStyle.WorkSheet = XLSXStyle.utils.json_to_sheet([]);

      const objValues = [
        { columnTitle: 'PROVIDER SYSTEM', columnKey: 'A1', colorKey: 'e7e6e6' },
        { columnTitle: 'Hospital Reference Lab', columnKey: 'H1', colorKey: 'D8E1F2' },
        { columnTitle: 'Site of Service', columnKey: 'J1', colorKey: 'ffffff' },
        { columnTitle: 'Medication Sourcing', columnKey: 'V1', colorKey: 'D8E1F2' },
        { columnTitle: 'Designated Diagnostic Provider', columnKey: 'Y1', colorKey: 'FFFFFF' },
        { columnTitle: 'IMPACT UPON REQUEST ONLY', columnKey: 'AC1', colorKey: 'ffff00' },
      ];

      objValues.forEach((item) => {
        ws[item.columnKey] = { t: 's', v: item.columnTitle };
        ws[item.columnKey].s = {
          fill: { fgColor: { rgb: item.colorKey } },
          font: { bold: true },
          alignment: { vertical: 'center', horizontal: 'center' },
        };
      });

      ws['!merges'] = ws['!merges'] || [];
      ws['!merges'].push(
        { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
        { s: { r: 0, c: 7 }, e: { r: 0, c: 8 } },
        { s: { r: 0, c: 9 }, e: { r: 0, c: 20 } },
        { s: { r: 0, c: 21 }, e: { r: 0, c: 23 } },
        { s: { r: 0, c: 24 }, e: { r: 0, c: 27 } },
        { s: { r: 0, c: 28 }, e: { r: 0, c: 31 } }
      );

      // Adding header with styles
      XLSXStyle.utils.sheet_add_aoa(ws, Heading);

      const ws2 = XLSXStyle.utils.sheet_add_json(ws, sheet.data, { origin: 'A2', skipHeader: false });
      ws2['!cols'] = [];

      // color to column and alternate color to the cell
      const columnData = ['I', 'L', 'O', 'R', 'T', 'W', 'Y', 'AA'];

      Object.keys(sheet.data[0] || {}).forEach((key, colIndex) => {
        const cellAddress = XLSXStyle.utils.encode_cell({ r: 1, c: colIndex });
        if (ws2[cellAddress]) {
          ws2[cellAddress].s = { alignment: { wrapText: true } };
        }
        const cellData = ['A1', 'A2', 'B2', 'C2', 'D2', 'E2', 'F2', 'G2'];
        const colorCode = 'e7e6e6';
        cellData.forEach((ca) => {
          if (!ws2[ca]) return;
          ws2[ca].s = {
            alignment: { wrapText: true, horizontal: 'center', vertical: 'center' },
            fill: { fgColor: { rgb: colorCode } },
          };
          if (ws2[ca].v === 'PROVIDER SYSTEM') {
            ws2[ca].s = {
              alignment: { wrapText: true, horizontal: 'center' },
              font: { bold: true },
              fill: { fgColor: { rgb: colorCode } },
            };
          }
        });
        ws2['!cols'].push({ width: 20 });
        columnData.forEach((ca) => {
          for (let row = 2; ; row += 1) {
            const cellAddressValue = `${ca}${row}`;
            const cell = ws2[cellAddressValue];
            if (!cell) {
              break;
            }
            cell.s = {
              alignment: { wrapText: true },
              fill: { fgColor: { rgb: this.getsummaryImpactColor(cell.v) } },
            };
          }
        });
      });

      XLSXStyle.utils.book_append_sheet(wb, ws, sheet.title.substr(0, 30));
    });
    XLSXStyle.writeFile(wb, options.fileName + options.fileFormat);
  }

  static async downloadMedsourceData(data: any, options: DownloadOptions) {
    const XLSXStyle = await loadXlsxStyleForExport();
    if (!XLSXStyle) return;
    const { sheets } = data;
    const storageService = new StorageService;
    const wb = XLSXStyle.utils.book_new();

    this.appendCoverPage(data, options, wb, XLSXStyle);

    // Append sheet containing data of the table
    sheets.forEach((sheet: any) => {
      const ws = XLSXStyle.utils.json_to_sheet(sheet.data);
      const range = XLSXStyle.utils.decode_range(ws['!ref']);
      range.e.c = 2;
      ws['!cols'] = [];
      if (sheet.data[0]) {
        for (let rowIndex = 0; rowIndex <= sheet.data.length; rowIndex++) {
          for (let colIndex = 0; colIndex <= Object.keys(sheet.data[0]).length - 1; colIndex++) {
            const cellStyle: any = {
              alignment: { wrapText: true, horizontal: 'center' }
            };
            ws['!cols'].push({ width: 15 });
            const cell = ws[XLSXStyle.utils.encode_cell({ r: rowIndex, c: colIndex })];
            if (rowIndex === 0) {
              cellStyle.font = { bold: true };
            }
            if (colIndex < 3 && !options.noStyling) {
              cellStyle.fill = { fgColor: { rgb: 'D9D9D9' } };
            }
            cell.s = cellStyle;
          }
        }
      }
      XLSXStyle.utils.book_append_sheet(wb, ws, sheet.title.substr(0, 30));
    });
    XLSXStyle.writeFile(wb, options.fileName + options.fileFormat);
    storageService.setAppEventDataLayerForAdobeOnExportData(options.fileName);
  }
}

export { DownloadService };